/**
 * Money the server already converted into one currency (group pages).
 * Unlike <Money />, this does NOT convert again — it formats `minor` in
 * the `currency` it was returned in.
 */
import { formatAmount, type MoneyFormatOptions } from "@/lib/currency";
import { cn } from "@/lib/utils";

export const fmtIn = (minor: number | null | undefined, currency: string, opts: MoneyFormatOptions = {}) =>
  minor == null || Number.isNaN(minor) ? "—" : formatAmount(minor / 100, currency, opts);

export const fmtPct = (v: number | null | undefined, signed = false) =>
  v == null ? "—" : `${signed && v > 0 ? "+" : ""}${v.toLocaleString("en-US", { maximumFractionDigits: 1 })}%`;

export default function FxMoney({
  minor,
  currency,
  signed,
  compact,
  className,
}: {
  minor: number | null | undefined;
  currency: string;
  signed?: boolean;
  compact?: boolean;
  className?: string;
}) {
  return (
    <span className={cn("whitespace-nowrap tabular-nums", signed && (minor ?? 0) < 0 && "text-rose-600 dark:text-rose-400", className)}>
      {fmtIn(minor, currency, { compact })}
    </span>
  );
}

/** "+12.5%" in green, "−4%" in red, "New" when there was nothing before. */
export function Growth({ pct, hadBefore = true, className }: { pct: number | null | undefined; hadBefore?: boolean; className?: string }) {
  if (pct == null) {
    return <span className={cn("text-xs text-muted-foreground", className)}>{hadBefore ? "—" : "New"}</span>;
  }
  const up = pct > 0;
  const flat = pct === 0;
  return (
    <span
      className={cn(
        "whitespace-nowrap text-xs font-medium tabular-nums",
        flat ? "text-muted-foreground" : up ? "text-[#2E7D32] dark:text-[#81C784]" : "text-rose-600 dark:text-rose-400",
        className,
      )}
    >
      {flat ? "0%" : `${up ? "▲" : "▼"} ${Math.abs(pct).toLocaleString("en-US", { maximumFractionDigits: 1 })}%`}
    </span>
  );
}
